import { RefreshCw } from "lucide-react";
import { useCallback, useState } from "react";
import { commandCenterApi } from "../../api/command-center.js";
import type { SessionContext } from "../../api/types.js";
import { InlineError, LoadingTable } from "../../components/AsyncState.js";
import { SupportedScopePanel } from "../../components/SupportedScopePanel.js";
import { useApiResource } from "../../hooks/useApiResource.js";
import { CoverageReportView } from "./CoverageReportView.js";

export function CoveragePage({
  session: _session,
  navigate,
  selectedScanId,
}: {
  session: SessionContext;
  navigate: (path: string) => void;
  selectedScanId?: string | undefined;
}) {
  const scopeKey = selectedScanId ?? "portfolio";
  const [paging, setPaging] = useState({ scopeKey, page: 1, heatmapPage: 1 });
  const page = paging.scopeKey === scopeKey ? paging.page : 1;
  const heatmapPage = paging.scopeKey === scopeKey ? paging.heatmapPage : 1;
  const loadCoverage = useCallback(
    (signal: AbortSignal) =>
      commandCenterApi.getCoverage(selectedScanId, page, signal, heatmapPage),
    [selectedScanId, page, heatmapPage],
  );
  const coverage = useApiResource(loadCoverage);

  return (
    <section className="page coverage-page" aria-labelledby="coverage-page-heading">
      <div className="page-heading">
        <div>
          <span className="page-kicker">Estate review</span>
          <h1 id="coverage-page-heading">Evidence</h1>
          <p>See what was assessed, how complete the evidence is, and what remains unknown.</p>
        </div>
        <button
          className="icon-button"
          type="button"
          aria-label="Refresh coverage"
          title="Refresh coverage"
          disabled={coverage.isRefreshing}
          onClick={() => {
            coverage.refresh();
          }}
        >
          <RefreshCw aria-hidden="true" size={17} />
        </button>
      </div>

      <SupportedScopePanel variant="compact" />

      {coverage.isLoading && !coverage.data ? <LoadingTable columns={7} rows={5} /> : null}
      {coverage.error && !coverage.data ? (
        <InlineError error={coverage.error} onRetry={coverage.refresh} />
      ) : null}
      {coverage.data ? (
        <CoverageReportView
          report={coverage.data}
          navigate={navigate}
          onPreviousPage={() =>
            setPaging({
              scopeKey,
              page: Math.max(1, (coverage.data?.matrixPage ?? page) - 1),
              heatmapPage,
            })
          }
          onNextPage={() =>
            setPaging({
              scopeKey,
              page: (coverage.data?.matrixPage ?? page) + 1,
              heatmapPage,
            })
          }
          onHeatmapPreviousPage={() =>
            setPaging({
              scopeKey,
              page,
              heatmapPage: Math.max(1, (coverage.data?.heatmapPage ?? heatmapPage) - 1),
            })
          }
          onHeatmapNextPage={() =>
            setPaging({
              scopeKey,
              page,
              heatmapPage: (coverage.data?.heatmapPage ?? heatmapPage) + 1,
            })
          }
        />
      ) : null}
    </section>
  );
}
